/**
 * Combat & Content Model V2 — shared primitive types.
 * ACTIVE vs RESERVED documented in docs/COMBAT_MODEL_V2.md
 */

/** ACTIVE: GROUND. RESERVED: AIR (schema only in M42). */
export type MovementClass = 'GROUND' | 'AIR'

/** ACTIVE: NORMAL, BOSS (compatibility). RESERVED: ELITE (no production content). */
export type EnemyRank = 'NORMAL' | 'ELITE' | 'BOSS'

/**
 * Damage types — describe damage interaction, NOT status effects.
 * ACTIVE mappings: FIRE→ENERGY, FROST→COLD, CANNON→EXPLOSIVE, POISON→TOXIC.
 * RESERVED: ARCANE (Mage), PIERCE (AA).
 */
export type DamageType = 'ENERGY' | 'EXPLOSIVE' | 'COLD' | 'TOXIC' | 'ARCANE' | 'PIERCE'

export const DAMAGE_TYPES: readonly DamageType[] = [
  'ENERGY',
  'EXPLOSIVE',
  'COLD',
  'TOXIC',
  'ARCANE',
  'PIERCE'
] as const

/** ACTIVE: NONE (all current enemies). RESERVED catalog expansion. */
export type ArmorType = 'NONE' | 'LIGHT' | 'HEAVY' | 'REINFORCED' | 'ARCANE'

export const ARMOR_TYPES: readonly ArmorType[] = [
  'NONE',
  'LIGHT',
  'HEAVY',
  'REINFORCED',
  'ARCANE'
] as const

export interface ArmorProfile {
  armorType: ArmorType
  /** Numeric strength — independent of category. 0 = no flat mitigation. */
  armorValue: number
  /**
   * Per-damage-type multipliers (data-driven).
   * 0 = immune, <1 resistant, 1 normal, >1 vulnerable. Never negative.
   */
  damageModifiers?: Partial<Record<DamageType, number>>
}

/** ACTIVE: GROUND/BOTH. CANNON = GROUND (future intentional). AIR unused until flyers. */
export type TargetClass = 'GROUND' | 'AIR' | 'BOTH'

export type DifficultyId = 'easy' | 'normal' | 'hard' | 'insane'

export const DIFFICULTY_IDS: readonly DifficultyId[] = [
  'easy',
  'normal',
  'hard',
  'insane'
] as const

export type GameModeId = 'CAMPAIGN' | 'ENDLESS' | 'CHALLENGE'

/** Match-local spendable currency. Player-facing label: Coins (internal field may remain `gold`). */
export type MatchCurrency = 'COINS'

/** Future persistent meta currency — NOT implemented in M42. */
export type MetaCurrency = 'INGOTS'

/** Derived mastery from difficulty clears — not spendable. */
export type MasteryMetric = 'STARS'
